import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{
    text: string;
    subitems?: string[];
  }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Set Up Production Database',
    description: 'Initialize a PostgreSQL database with critical production data that needs to be protected through backup and disaster recovery procedures.',
    requirements: [
      {
        text: 'Verify PostgreSQL is running and enabled'
      },
      {
        text: 'Create a production database with sample business data'
      },
      {
        text: 'Create tables for customers, orders, and products'
      },
      {
        text: 'Populate the database with sample production data'
      },
      {
        text: 'Configure database authentication and access permissions'
      },
      {
        text: 'Document the initial production database state in /root/initial_database_audit.txt'
      }
    ],
    verification: [
      'PostgreSQL is installed and running',
      'Production database exists with data',
      'All required tables are created',
      'Sample data is successfully inserted',
      'Database is accessible and operational',
      'Initial data state is documented in /root/initial_database_audit.txt'
    ]
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Implement Backup Strategy',
    description: 'Create a comprehensive backup strategy including full, incremental, and automated backups with proper storage and retention policies.',
    requirements: [
      {
        text: 'Set /var/backups/postgres ownership to postgres:postgres and mode 700'
      },
      {
        text: 'Perform a full database backup using pg_dump'
      },
      {
        text: 'Create a backup script that includes:',
        subitems: [
          'Timestamp-based naming convention',
          'Compression to save storage space',
          'Backup verification checks using gzip -t or gunzip -t',
          'Logging of backup operations to /var/log/backup.log with BACKUP SUCCESS'
        ]
      },
      {
        text: 'Required paths (for grading):',
        subitems: [
          'Backup script at /usr/local/bin/backup_postgres.sh (must be executable)',
          'Compressed backup files named production-*.sql.gz',
          'Local backup directory at /var/backups/postgres',
          'Remote backup directory at /var/backups/remote',
          'Metadata file at /var/backups/postgres/backup_metadata.json'
        ]
      },
      {
        text: 'Set up automated scheduled backups using cron'
      },
      {
        text: 'Implement backup rotation policy with mtime +7 delete logic'
      },
      {
        text: 'Include /opt/critical_config.conf in local backup storage'
      }
    ],
    verification: [
      'Backup directory exists with postgres:postgres ownership and mode 700',
      'Full database backup completed successfully',
      'Backup script is created and executable',
      'Automated backup schedule is configured',
      'Backup rotation policy is in place',
      'Backup metadata is properly maintained and critical_config.conf is backed up'
    ]
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Configure Remote Backup Storage',
    description: 'Set up remote backup storage to protect against local system failures and implement secure transfer of backup files.',
    requirements: [
      {
        text: 'Create /var/backups/remote as the simulated remote backup location'
      },
      {
        text: 'Implement secure backup transfer using rsync or scp'
      },
      {
        text: 'Set up SSH key-based authentication for automated transfers'
      },
      {
        text: 'Create executable /usr/local/bin/sync_remote_backups.sh for remote backups'
      },
      {
        text: 'Verify remote backup integrity after transfer'
      },
      {
        text: 'Copy production-*.sql.gz and critical_config.conf to /var/backups/remote'
      }
    ],
    verification: [
      'Remote backup location is configured',
      'Secure transfer mechanism is working',
      'SSH authentication is properly set up',
      'Remote synchronization script exists and is executable',
      'Remote backups are verified for integrity',
      'critical_config.conf is present in local and remote backup storage'
    ]
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Create Disaster Recovery Procedures',
    description: 'Develop and document comprehensive disaster recovery procedures including recovery time objectives (RTO) and recovery point objectives (RPO).',
    requirements: [
      {
        text: 'Create /root/disaster_recovery.txt with step-by-step procedures'
      },
      {
        text: 'Document recovery procedures for different failure scenarios:',
        subitems: [
          'Data corruption recovery',
          'Accidental deletion recovery',
          'Complete system failure recovery',
          'Point-in-time recovery procedures'
        ]
      },
      {
        text: 'Create recovery validation checklist'
      },
      {
        text: 'Set up recovery environment for testing'
      },
      {
        text: 'Document RTO and RPO objectives in /root/disaster_recovery.txt'
      },
      {
        text: 'Create incident response contact list and escalation procedures'
      }
    ],
    verification: [
      'Disaster recovery runbook exists at /root/disaster_recovery.txt',
      'All failure scenarios are documented',
      'Recovery validation checklist is created',
      'Recovery environment is prepared',
      'RTO/RPO objectives are clearly defined',
      'Contact list and procedures are documented'
    ]
  },
  {
    id: 'task-5',
    number: 5,
    title: 'Test Disaster Recovery',
    description: 'Simulate a disaster scenario and execute the complete recovery process to validate backup integrity and recovery procedures.',
    requirements: [
      {
        text: 'Simulate a disaster by corrupting or removing production data'
      },
      {
        text: 'Execute the documented recovery procedures'
      },
      {
        text: 'Restore the most recent backup into production_recovery_test'
      },
      {
        text: 'Verify data integrity after restoration'
      },
      {
        text: 'Test point-in-time recovery to a specific timestamp'
      },
      {
        text: 'Write RECOVERY TEST PASSED to /var/backups/recovery_test.log after verifying row counts'
      },
      {
        text: 'Update procedures based on test results'
      }
    ],
    verification: [
      'Disaster simulation completed successfully',
      'Recovery procedures were executed',
      'production_recovery_test is fully restored from backup',
      'Data integrity is verified and confirmed',
      'Point-in-time recovery works correctly',
      'Recovery metrics are documented in /var/backups/recovery_test.log',
      'Procedures are updated based on findings'
    ]
  },
  {
    id: 'task-6',
    number: 6,
    title: 'Implement Monitoring and Alerting',
    description: 'Set up monitoring for backup operations and configure alerts for backup failures or issues to ensure backup reliability.',
    requirements: [
      {
        text: 'Create executable /usr/local/bin/backup_monitor.sh to check backup health'
      },
      {
        text: 'Monitor backup size and growth trends'
      },
      {
        text: 'Set up alerts for backup failures'
      },
      {
        text: 'Create backup status dashboard or report'
      },
      {
        text: 'Implement backup verification automated tests'
      },
      {
        text: 'Configure log rotation for backup logs'
      },
      {
        text: 'Write BACKUP MONITOR OK to /var/log/backup_monitor.log on a healthy check'
      }
    ],
    verification: [
      'Monitoring script is executable at /usr/local/bin/backup_monitor.sh',
      'Backup metrics are being tracked',
      'Alert system is configured and tested',
      'Status reporting is operational',
      'Automated verification is in place',
      'Log rotation is configured',
      'Monitoring log contains BACKUP MONITOR OK'
    ]
  }
];

const BackupDisasterRecoveryInstructions: React.FC = () => {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());

  const handleTaskClick = (index: number) => {
    setCurrentTaskIndex(index);
  };

  const handleNextTask = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    }
  };

  const handlePreviousTask = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const checkId = `${taskId}-check-${checkIndex}`;
    const newChecks = new Set(completedChecks);
    
    if (newChecks.has(checkId)) {
      newChecks.delete(checkId);
    } else {
      newChecks.add(checkId);
    }
    
    setCompletedChecks(newChecks);

    // Check if all verifications for this task are completed
    const currentTask = tasks[currentTaskIndex];
    const allChecksCompleted = currentTask.verification.every(
      (_, idx) => newChecks.has(`${currentTask.id}-check-${idx}`)
    );

    if (allChecksCompleted && !completedTasks.has(currentTaskIndex)) {
      const newCompletedTasks = new Set(completedTasks);
      newCompletedTasks.add(currentTaskIndex);
      setCompletedTasks(newCompletedTasks);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      {/* Content Area - Increased to 1800px for less scrolling */}
      <div className="flex h-[1800px]">
        {/* Sidebar Navigation */}
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
              } text-[#1a1f2e]`}>
                {task.number}
              </div>
              <div className={`text-sm ${index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'}`}>
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">
            {currentTask.description}
          </p>

          {/* Requirements Section */}
          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>
            
            {currentTask.requirements.map((requirement, reqIndex) => (
              <div key={reqIndex} className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all">
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">{reqIndex + 1}. {requirement.text}</span>
                  {requirement.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {requirement.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Verification Section */}
          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              Verification Checklist
            </h4>
            
            {currentTask.verification.map((item, index) => (
              <label 
                key={index} 
                className="flex items-start mb-3 cursor-pointer hover:bg-[#1e293b] p-2 rounded-md transition-all group"
              >
                <input 
                  type="checkbox" 
                  className="mt-0.5 mr-3 w-4 h-4 text-[#10b981] bg-[#1a1f2e] border-[#475569] rounded focus:ring-[#10b981] focus:ring-2 cursor-pointer"
                  checked={completedChecks.has(`${currentTask.id}-check-${index}`)}
                  onChange={() => toggleVerification(currentTask.id, index)}
                />
                <span className={`text-sm leading-[1.6] transition-all ${
                  completedChecks.has(`${currentTask.id}-check-${index}`) 
                    ? 'text-[#10b981] line-through opacity-80' 
                    : 'text-[#cbd5e1] group-hover:text-[#e2e8f0]'
                }`}>
                  {item}
                </span>
              </label>
            ))}
          </div>

          {/* Progress Indicator */}
          <div className="mt-10 border-t border-[#2d3748] pt-6">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs text-[#94a3b8] uppercase tracking-[1px]">
                Task Progress
              </span>
              <span className="text-xs text-[#94a3b8]">
                {currentTaskIndex + 1} of {tasks.length} tasks
              </span>
            </div>
            
            {/* Progress Bar */}
            <div className="h-[6px] bg-[#1a1f2e] rounded-full overflow-hidden mb-6">
              <div 
                className="h-full bg-gradient-to-r from-[#10b981] to-[#059669] transition-all duration-300 ease-out"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between">
              <button 
                onClick={handlePreviousTask}
                disabled={currentTaskIndex === 0}
                className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center ${
                  currentTaskIndex === 0 
                    ? 'bg-[#1a1f2e] text-[#475569] cursor-not-allowed' 
                    : 'bg-[#2d3748] text-[#cbd5e1] hover:bg-[#374151] hover:text-[#e2e8f0]'
                }`}
              >
                <span className="mr-2">←</span>
                Previous
              </button>
              
              <button 
                onClick={handleNextTask}
                disabled={currentTaskIndex === tasks.length - 1}
                className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center ${
                  currentTaskIndex === tasks.length - 1
                    ? 'bg-[#1a1f2e] text-[#475569] cursor-not-allowed' 
                    : 'bg-gradient-to-r from-[#10b981] to-[#059669] text-[#1a1f2e] hover:from-[#0ea968] hover:to-[#047857]'
                }`}
              >
                Next Task
                <span className="ml-2">→</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackupDisasterRecoveryInstructions;
